/**
 * AlertsPage.jsx - SIMPLE ET MINIMALISTE
 *
 * ✅ Vue Liste: Pagination classique
 * ✅ Vue Grid: InfiniteScroll + CardGrid (comme SearchPage)
 * ✅ Pas de hook personnalisé
 * ✅ Code simple et lisible
 * ✅ FIX: Une seule requête (pas de race condition)
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import { List, Button, Empty, Spin, Tabs, Tag, message, Modal, Image, Pagination, Segmented } from "antd";
import { ArrowLeftOutlined, EyeOutlined } from "@ant-design/icons";
import { getSearchAlerts, getSavedSearches } from "../api/api";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { formatDateTime } from "../utils/formatters";
import CardGrid from "../components/Cards/CardGrid";

/**
 * AlertsPage - Alertes d'une recherche
 */
export default function AlertsPage() {
    const { searchId } = useParams();
    const navigate = useNavigate();
    const { isDarkMode } = useTheme();
    const { token } = useAuth();

    // État général
    const [searchName, setSearchName] = useState("Recherche");
    const [filterType, setFilterType] = useState("all");
    const [viewMode, setViewMode] = useState("list");
    const [selectedImage, setSelectedImage] = useState(null);

    // Vue Liste (Pagination)
    const [listAlerts, setListAlerts] = useState([]);
    const [listPage, setListPage] = useState(1);
    const [listLoading, setListLoading] = useState(true);
    const [listTotal, setListTotal] = useState(0);
    const [listTotalPages, setListTotalPages] = useState(0);

    // Vue Grid (InfiniteScroll)
    const [gridAlerts, setGridAlerts] = useState([]);
    const [gridPage, setGridPage] = useState(1);
    const [gridHasMore, setGridHasMore] = useState(true);

    // Totaux
    const [totals, setTotals] = useState({
        all: 0,
        start_sell_today: 0,
        saled: 0,
        change_price: 0,
    });

    // ============================================
    // FETCH DONNÉES (Recherche + Liste combinées)
    // ============================================

    useEffect(() => {
        if (!token || viewMode !== "list") return;

        const fetchData = async () => {
            try {
                setListLoading(true);

                // Fetch recherche
                try {
                    const searchRes = await getSavedSearches();
                    const search = searchRes.data.searches?.find((s) => s.id == searchId);
                    if (search) setSearchName(search.name);
                } catch (err) {
                    console.error("Erreur recherche:", err);
                }

                // Fetch liste
                const params = { page: listPage, per_page: 20 };
                if (filterType !== "all") params.change_type = filterType;
                const res = await getSearchAlerts(searchId, params);

                setListAlerts(res.data.alerts || []);
                setListTotal(res.data.total || 0);
                setListTotalPages(res.data.total_pages || 0);
                setTotals(res.data.per_type_totals || {});

            } catch (err) {
                console.error(err);
                message.error("Erreur chargement alertes");
            } finally {
                setListLoading(false);
            }
        };

        fetchData();
    }, [searchId, listPage, filterType, viewMode, token]);

    // ============================================
    // FETCH GRID (InfiniteScroll)
    // ============================================

    const fetchGrid = async (pageNum = 1) => {
        try {
            const params = { page: pageNum, per_page: 20 };
            if (filterType !== "all") params.change_type = filterType;
            const res = await getSearchAlerts(searchId, params);
            const data = res.data.alerts || [];

            // Premier fetch: remplace, sinon ajoute
            setGridAlerts((prev) => (pageNum === 1 ? data : [...prev, ...data]));
            setTotals(res.data.per_type_totals || {});
            setGridHasMore(data.length >= 20);
        } catch (err) {
            console.error(err);
            message.error("Erreur chargement alertes");
        }
    };

    useEffect(() => {
        if (!token || viewMode !== "grid") return;
        setGridPage(1);
        setGridAlerts([]);
        setGridHasMore(true);
        fetchGrid(1);
    }, [searchId, filterType, viewMode, token]);

    // ============================================
    // CHANGEMENT FILTRE (réinitialise pagination)
    // ============================================

    useEffect(() => {
        setListPage(1);
        setGridPage(1);
        setGridAlerts([]);
    }, [filterType]);

    // ============================================
    // UTILITAIRES
    // ============================================

    const getAlertColor = (changeType) => {
        const colors = {
            start_sell_today: "green",
            saled: "orange",
            price_change: "blue",
            change_price: "blue",
        };
        return colors[changeType] || "default";
    };

    const getAlertLabel = (changeType) => {
        const labels = {
            start_sell_today: "Mise en vente",
            saled: "Vendue",
            price_change: "Prix changé",
            change_price: "Prix changé",
        };
        return labels[changeType] || changeType;
    };

    const fetchMoreGrid = () => {
        const nextPage = gridPage + 1;
        setGridPage(nextPage);
        fetchGrid(nextPage);
    };

    // ============================================
    // RENDER
    // ============================================

    const tabs = [
        { key: "all", label: `Tous (${totals.all})` },
        { key: "start_sell_today", label: `Mise en vente (${totals.start_sell_today})` },
        { key: "saled", label: `Vendues (${totals.saled})` },
        { key: "change_price", label: `Prix changé (${totals.change_price})` },
    ];

    return (
        <div style={{ padding: "24px", minHeight: "100vh", background: isDarkMode ? "#000000" : "#fafafa" }}>
            <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
                {/* Bouton retour */}
                <Button
                    type="text"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate("/saved-searches")}
                    style={{ marginBottom: "16px" }}
                >
                    Retour aux recherches
                </Button>

                {/* Header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
                    <div>
                        <h2 style={{ fontSize: "24px", fontWeight: "600", margin: 0 }}>
                            Alertes : {searchName}
                        </h2>
                        <p style={{ opacity: 0.6, marginTop: "8px" }}>
                            {totals.all > 0 ? `${totals.all} alerte${totals.all > 1 ? "s" : ""}` : "Aucune alerte"}
                        </p>
                    </div>

                    {/* Toggle Vue */}
                    {totals.all > 0 && (
                        <Segmented
                            value={viewMode}
                            onChange={setViewMode}
                            options={[
                                { label: "📋 Liste", value: "list" },
                                { label: "🔲 Grille", value: "grid" },
                            ]}
                        />
                    )}
                </div>

                {totals.all === 0 ? (
                    <Empty description="Aucune alerte pour cette recherche" />
                ) : (
                    <>
                        {/* Tabs */}
                        <Tabs
                            activeKey={filterType}
                            onChange={setFilterType}
                            items={tabs}
                            style={{ marginBottom: "24px" }}
                        />

                        {/* VUE LISTE */}
                        {viewMode === "list" && (
                            <>
                                {listLoading && listAlerts.length === 0 ? (
                                    <div style={{ textAlign: "center", padding: "48px" }}>
                                        <Spin />
                                    </div>
                                ) : listAlerts.length === 0 ? (
                                    <Empty description="Aucune alerte pour ce filtre" />
                                ) : (
                                    <>
                                        <List
                                            dataSource={listAlerts}
                                            renderItem={(alert) => (
                                                <List.Item
                                                    style={{
                                                        background: isDarkMode ? "#1f1f1f" : "#ffffff",
                                                        padding: "16px",
                                                        marginBottom: "12px",
                                                        borderRadius: "6px",
                                                        border: `1px solid ${isDarkMode ? "#434343" : "#d9d9d9"}`,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "16px",
                                                    }}
                                                >
                                                    {/* Image */}
                                                    {alert.card?.image_path && (
                                                        <div
                                                            onClick={() => setSelectedImage(alert.card.image_path)}
                                                            style={{ cursor: "pointer", flexShrink: 0 }}
                                                        >
                                                            <img
                                                                src={alert.card.image_path}
                                                                alt={alert.card.name}
                                                                style={{
                                                                    width: "60px",
                                                                    height: "60px",
                                                                    borderRadius: "4px",
                                                                    objectFit: "cover",
                                                                    border: `2px solid ${isDarkMode ? "#434343" : "#d9d9d9"}`,
                                                                    transition: "transform 0.2s",
                                                                }}
                                                                onMouseEnter={(e) => (e.target.style.transform = "scale(1.05)")}
                                                                onMouseLeave={(e) => (e.target.style.transform = "scale(1)")}
                                                            />
                                                        </div>
                                                    )}

                                                    {/* Contenu */}
                                                    <div style={{ flex: 1 }}>
                                                        <List.Item.Meta
                                                            title={<strong>{alert.card?.name}</strong>}
                                                            description={
                                                                <div style={{ fontSize: "12px", opacity: 0.6 }}>
                                                                    <Tag color={getAlertColor(alert.change_type)}>
                                                                        {getAlertLabel(alert.change_type)}
                                                                    </Tag>
                                                                    <span style={{ marginLeft: "12px" }}>
                                                                        {formatDateTime(alert.created_at)}
                                                                    </span>
                                                                </div>
                                                            }
                                                        />
                                                    </div>

                                                    {/* Bouton */}
                                                    <Button
                                                        type="primary"
                                                        icon={<EyeOutlined />}
                                                        onClick={() => navigate(`/cards/${alert.card_id}`)}
                                                    >
                                                        Voir
                                                    </Button>
                                                </List.Item>
                                            )}
                                        />

                                        {/* Pagination */}
                                        {listTotalPages > 1 && (
                                            <div style={{ textAlign: "center", marginTop: "24px" }}>
                                                <Pagination
                                                    current={listPage}
                                                    total={listTotal}
                                                    pageSize={20}
                                                    onChange={setListPage}
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </>
                        )}

                        {/* VUE GRID (InfiniteScroll) */}
                        {viewMode === "grid" && (
                            <InfiniteScroll
                                dataLength={gridAlerts.length}
                                next={fetchMoreGrid}
                                hasMore={gridHasMore}
                                loader={
                                    <h4 style={{ textAlign: "center", padding: "16px" }}>
                                        Chargement...
                                    </h4>
                                }
                            >
                                <CardGrid cards={gridAlerts.map((alert) => alert.card)} />
                            </InfiniteScroll>
                        )}
                    </>
                )}
            </div>

            {/* MODAL IMAGE */}
            <Modal
                open={!!selectedImage}
                footer={null}
                onCancel={() => setSelectedImage(null)}
                centered
                width={600}
                bodyStyle={{ padding: 0 }}
            >
                {selectedImage && (
                    <Image
                        src={selectedImage}
                        alt="Carte"
                        preview={false}
                        style={{ width: "100%", borderRadius: "6px" }}
                    />
                )}
            </Modal>
        </div>
    );
}